/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { DockerHost } from "@/api/docker";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtBytes } from "@/components/servers/kit";
import { Button } from "@/components/ui/Button";

export type HostView = "dashboard" | "containers" | "stacks" | "images" | "volumes" | "networks";

export const fmtSize = (n?: number) => (n === undefined || n === null || n < 0 ? "—" : fmtBytes(n));

export function fmtDate(unixOrIso: number | string | undefined): string {
  if (unixOrIso === undefined || unixOrIso === "") return "—";
  const d = typeof unixOrIso === "number" ? new Date(unixOrIso * 1000) : new Date(unixOrIso);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1971) return "—";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Address to put in front of a published port: the Docker host's own, else this page's. */
export function hostAddress(host: DockerHost): string {
  const m = /^tcp:\/\/([^:/]+)/.exec(host.host);
  return m ? m[1] : window.location.hostname;
}

export function shortID(id: string): string {
  return id.replace(/^sha256:/, "").slice(0, 12);
}

const STATE_TONE: Record<string, string> = {
  running: "ok",
  exited: "off",
  created: "off",
  paused: "warn",
  restarting: "bad",
  dead: "bad",
  removing: "warn",
};

export const StateBadge: React.FC<{ state: string }> = ({ state }) => (
  <span className={`dkr-state dkr-state--${STATE_TONE[state] ?? "off"}`}>
    <i />
    {state}
  </span>
);

export const ConfirmDialog: React.FC<{
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ open, title, message, confirmLabel, danger, busy, onConfirm, onClose }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{message}</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="button" variant={danger ? "destructive" : "default"} size="sm" onClick={onConfirm} isLoading={busy}>
          {confirmLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const PortLinks: React.FC<{ ports: { ip?: string; private: number; public?: number; type: string }[]; address: string }> = ({ ports, address }) => {
  const published = ports.filter((p) => p.public);
  const seen = new Set<string>();
  const uniq = published.filter((p) => {
    const k = `${p.public}:${p.private}/${p.type}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  if (uniq.length === 0) return <span className="dkr-dim">—</span>;
  return (
    <span className="dkr-ports">
      {uniq.map((p) => {
        const label = `${p.public}:${p.private}`;
        return p.type === "tcp" ? (
          <a key={label} href={`http://${address}:${p.public}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={`Open ${address}:${p.public}`}>
            {label}
          </a>
        ) : (
          <span key={label} title={p.type}>
            {label}/{p.type}
          </span>
        );
      })}
    </span>
  );
};
